// Spreadsheet downloads (Excel .xlsx and CSV) for every table in the tracker, and a
// print-friendly list for one election's tasks. 2.0.2 (suite task 20, "every table exports"):
// this used to be tasks-as-CSV only; each table is now one rows builder below, and the same rows
// go into either file, so the CSV and the Excel file of a table always hold the same thing.

import JSZip from "jszip";

export const STATUS_LABELS = { not_started: "Not Started", in_progress: "In Progress", blocked: "Blocked", completed: "Completed" };

// Spreadsheet apps run a cell starting with = + - @ (or a tab or carriage return, which some skip
// first) as a formula; a task title like "=HYPERLINK(...)" typed by anyone would otherwise run on
// whoever opens the export. A leading apostrophe makes it plain text. The Excel cells are written
// as text anyway, but they get it too: an .xlsx saved again as CSV would bring the formula back.
export function neutralize(v) {
  const s = v == null ? "" : String(v);
  return /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
}

function csvCell(v) {
  const s = neutralize(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// rows: [[header...], [cell...], ...]. The BOM makes Excel read accented names correctly.
export function rowsToCsv(rows) {
  return "﻿" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

// ---------- tables as rows ----------

// electionName (a function), when given, adds an Election column for lists that span elections.
export function taskRows(tasks, categoryLabel, electionName) {
  const header = ["Due date", "Milestone", "Title", "Category", "Status", "Assigned to", "Description", "Document / link"];
  const withElection = typeof electionName === "function";
  return [
    withElection ? ["Election", ...header] : header,
    ...tasks.map((t) => {
      const row = [
        t.dueDate,
        t.label,
        t.title,
        categoryLabel(t.category),
        STATUS_LABELS[t.status] || t.status,
        (t.assignees || []).map((a) => a.name || a.email).join("; "),
        t.description,
        t.docRef,
      ];
      return withElection ? [electionName(t), ...row] : row;
    }),
  ];
}

export function tasksToCsv(tasks, categoryLabel) {
  return rowsToCsv(taskRows(tasks, categoryLabel));
}

// elections: [{ id, name, date }]; tasks: every task, to count each election's.
export function electionRows(elections, tasks) {
  return [
    ["Election", "Election Day", "Tasks", "Completed", "Not completed"],
    ...elections.map((e) => {
      const mine = tasks.filter((t) => t.electionId === e.id);
      const done = mine.filter((t) => t.status === "completed").length;
      return [e.name, e.date, String(mine.length), String(done), String(mine.length - done)];
    }),
  ];
}

// people: the pick list; countFor(id): tasks assigned to them across all elections.
export function peopleRows(people, countFor) {
  return [["Name", "Email", "Tasks assigned (all elections)"], ...people.map((p) => [p.name || "", p.email || "", String(countFor(p.id))])];
}

// entries: the audit trail as the Admin screen builds it ({ at, by, text, task, election }).
export function auditRows(entries) {
  return [
    ["When (UTC)", "Who", "What changed", "Task", "Election"],
    ...entries.map((e) => [utcStamp(e.at), e.by || "", e.text || "", e.task?.title || "", e.election ? e.election.name : "No election"]),
  ];
}

// "2026-10-19 14:05 UTC": the same for everyone, whatever their time zone.
export function utcStamp(at) {
  const t = Date.parse(at);
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 16).replace("T", " ") + " UTC" : "";
}

// ---------- Excel (.xlsx) ----------
// An .xlsx file is a zip of XML parts; JSZip (already used for backups) makes the zip. Every cell
// is an inline text string in a text-formatted column, so Excel keeps leading zeros and never
// turns "3-4" into a date. The first row is bold and frozen.

const xmlEsc = (s) => String(s)
  .replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]))
  // Control characters other than tab and newline aren't allowed in XML, and Excel refuses
  // the whole file over one; drop them.
  .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "");
function colName(i) { let s = ""; i++; while (i) { const r = (i - 1) % 26; s = String.fromCharCode(65 + r) + s; i = Math.floor((i - 1) / 26); } return s; }

const NS = 'xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"';
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

// sheets: [{ name, rows }]. Returns a Blob, or with type "uint8array" the bytes (for tests).
export function rowsToXlsx(sheets, type = "blob") {
  const zip = new JSZip();
  zip.file("[Content_Types].xml", `${HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("") + "</Types>");
  zip.file("_rels/.rels", `${HEAD}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`);
  zip.file("xl/workbook.xml", `${HEAD}<workbook ${NS} xmlns:r="${REL}"><sheets>` +
    sheets.map((s, i) => `<sheet name="${xmlEsc(String(s.name).replace(/[\\/?*[\]:]/g, " ").slice(0, 31))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("") + "</sheets></workbook>");
  zip.file("xl/_rels/workbook.xml.rels", `${HEAD}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="${REL}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join("") +
    `<Relationship Id="rId${sheets.length + 1}" Type="${REL}/styles" Target="styles.xml"/></Relationships>`);
  zip.file("xl/styles.xml", `${HEAD}<styleSheet ${NS}><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf/></cellStyleXfs><cellXfs count="3"><xf/><xf fontId="1" applyFont="1"/><xf numFmtId="49" applyNumberFormat="1"><alignment wrapText="1" vertical="top"/></xf></cellXfs></styleSheet>`);
  sheets.forEach((s, i) => {
    const width = Math.max(1, ...s.rows.map((r) => r.length));
    // Column widths from the longest first line in the column, 8 to 60 characters.
    const cols = Array.from({ length: width }, (_, c) => {
      const w = Math.min(60, Math.max(8, ...s.rows.map((r) => String(r[c] ?? "").split("\n")[0].length + 2)));
      return `<col min="${c + 1}" max="${c + 1}" width="${w}" customWidth="1" style="2"/>`;
    }).join("");
    const rows = s.rows.map((r, ri) => `<row r="${ri + 1}">` + r.map((v, ci) => {
      const text = neutralize(v);
      return text === "" ? "" : `<c r="${colName(ci)}${ri + 1}" t="inlineStr" s="${ri === 0 ? 1 : 2}"><is><t xml:space="preserve">${xmlEsc(text)}</t></is></c>`;
    }).join("") + "</row>").join("");
    zip.file(`xl/worksheets/sheet${i + 1}.xml`, `${HEAD}<worksheet ${NS}><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${cols}</cols><sheetData>${rows}</sheetData></worksheet>`);
  });
  return zip.generateAsync({ type, mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

// "General Election" -> "general-election"; used in every download's name.
export function fileSlug(s) {
  return String(s || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "export";
}

// One table to a file. format: "xlsx" or "csv"; name: the file name without its extension.
export async function downloadTable(format, name, sheetName, rows) {
  if (format === "xlsx") downloadBlobAs(await rowsToXlsx([{ name: sheetName, rows }]), `${name}.xlsx`);
  else downloadText(rowsToCsv(rows), `${name}.csv`, "text/csv;charset=utf-8");
}

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export function printTasks({ tasks, election, orgName, categoryLabel, formatDate }) {
  const w = window.open("", "_blank");
  if (!w) return false;
  const rows = tasks
    .map(
      (t) => `<tr>
        <td class="nowrap">${esc(formatDate(t.dueDate))}<div class="muted">${esc(t.label)}</div></td>
        <td><b>${esc(t.title)}</b>${t.description ? `<div class="muted">${esc(t.description)}</div>` : ""}</td>
        <td>${esc(categoryLabel(t.category))}</td>
        <td>${esc(STATUS_LABELS[t.status] || t.status)}</td>
        <td>${esc((t.assignees || []).map((a) => a.name || a.email).join(", "))}</td>
        <td class="box"></td>
      </tr>`
    )
    .join("");
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(election.name)} tasks</title>
<style>
  body { font-family: "Public Sans", system-ui, sans-serif; color: #18213a; margin: 24px; }
  h1 { font-family: Merriweather, Georgia, serif; font-size: 20px; margin: 0; }
  .sub { color: #56607a; margin: 4px 0 16px; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { border-bottom: 1px solid #d9dde7; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #1b2a4a; color: #fff; }
  .muted { color: #56607a; font-size: 11px; }
  .nowrap { white-space: nowrap; }
  .box { width: 28px; }
  @media print { body { margin: 0; } tr { page-break-inside: avoid; } }
</style></head><body>
<h1>${esc(election.name)}: task list</h1>
<div class="sub">${esc(orgName)} · Election Day ${esc(formatDate(election.date))} · ${tasks.length} task${tasks.length === 1 ? "" : "s"} · printed ${esc(new Date().toLocaleDateString())}</div>
<table><thead><tr><th>Due</th><th>Task</th><th>Category</th><th>Status</th><th>Assigned to</th><th>Done</th></tr></thead><tbody>${rows}</tbody></table>
<script>window.onload = function () { window.print(); };</script>
</body></html>`);
  w.document.close();
  return true;
}

export function downloadText(text, filename, type) {
  downloadBlobAs(new Blob([text], { type }), filename);
}

function downloadBlobAs(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
